
import React, { useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import { invert } from "polished";
import { NavLink } from "react-router-dom";
import { absoluteURL, relativeURL } from "../../../../util/UrlHelper";
import { MenuItem } from "../../../../typings/typings";
import ImageCrop from "../Image/ImageCrop";
import { getImageCropUrl } from "../../../../util/MediaHelper";
import {isDevelopment} from "../../../../util/ContextHelper";

const CustomMenu = styled.div`
    background-color: transparent;
    background-size: cover;
    position: fixed;
    width: 100%;
    height: 100%;
    left: 0;
    top: 0;
    padding: 200px 5rem;
    box-sizing: border-box;
    transition: all .2s linear;
    opacity: 0;
    pointer-events: none;

    &.custom-menu--active{
        opacity: 1;
        pointer-events: all;
        > div > div:not(.backgrounds), a{
            animation: moveUpFast .5s both;
        }
        a:first-child + .backgrounds{
            opacity: 1;
        }

    }

    > div > a {
        font-size: 30px;
        letter-spacing: 0.15em;
        display: inline-block;
        text-align: left;
        margin-bottom: 20px;
        position: relative;
        span {
            position: relative;
            &:before{
                content: "";
                position: absolute;
                width: 100%;
                max-width: 0%;
                border: 0;
                left: 0;
                bottom: -3px;
                transition: max-width .2s ease-in-out;
            }
        }
        &:hover {
            span:before {
                max-width: 100%;
                border: 2px solid;
            }
        }
        &.active{
            & + div.sub-menu{
                opacity: 1;
                pointer-events: all;
                h4 {
                    animation: moveUp .5s cubic-bezier(.01,.81,.11,1) both;
                }
                ul {
                    animation: moveUp .5s cubic-bezier(.01,.81,.11,1) both;
                    animation-delay: .1s;
                }
            }
        }
    }

    a {
        word-break: break-word;
        text-transform: uppercase;
        font-weight: bold;
    }

    @keyframes moveUp{
        0%{
            transform: translateY(100px);
        }
        100%{
            transform: translateY(0px);
        }
    }
    @keyframes moveUpFast{
        0%{
            transform: translateY(30px);
        }
        100%{
            transform: translateY(0px);
        }
    }
    .sub-menu{
        display: block;
        width: 50%;
        position: absolute;
        right: -50%;
        top: 0;
        pointer-events: none;
        opacity: 0;
        transition: all .5s linear;
        padding-left: 30px;
        padding-top: 30px;
        margin-top: 10px;
        &:before{
            content: "";
            width: 2px;
            border-left: 1px solid;
            height: 100px;
            position: absolute;
            margin-left: -30px;
            margin-top: -30px;
        }
        h4{
            font-size: 14px;
            font-weight: bold;
            letter-spacing: 1px;
        }
        a {
            text-transform: none;
            font-weight: normal;
        }
        ul{
            li{
                width: 50%;
                float: left;
                padding-right: 1rem;
                a{
                    &:first-letter{
                        text-transform: uppercase;
                    }
                    font-weight: normal;
                    font-size: 14px;
                    padding-bottom: 6px;
                    display: inline-block;
                    position: relative;
                    &:before{
                        content: "";
                        position: absolute;
                        width: 0%;
                        border: 0px solid;
                        left: 0;
                        bottom: 3px;
                        transition: all .2s ease-in-out;
                    }

                    &:hover{
                        &:before{
                            width: 100%;
                            border: 1px solid;
                        }
                    }
                }
            }
        }
    }

    .backgrounds{
        position: fixed;
        top: 0;
        height: 100vh;
        z-index: -1;
        left: 0;
        width: 100%;
        transition: opacity .5s linear;
        pointer-events: none;

        img{
            position: absolute;
            top: 0;
            object-fit: cover;
            width: 100%;
            height: 100%;
            opacity: 0;
            transition: opacity .2s linear, filter 1s linear;

            &.active {
                opacity: 1;
            }

            &.lazy-loaded{
                filter: blur(0px);
            }
        }
    }
`;

interface Props
{
    id: number;
    menuItems: MenuItem[];
    textColor: string;
    menuItemOnClick: (menuItem: MenuItem) => void;
}

const CustomMegadropdown: React.FC<Props> = (props) =>
{
    const { id: rootmenuId, menuItems } = props;
    const [currentLink, setActiveLink] = useState<MenuItem>();
    const [currentBg, setActiveBg] = useState<MenuItem>();
    useEffect(() =>
    {
        const activeRootLink = menuItems.find((item) => item.id === rootmenuId);
        setActiveBg(activeRootLink);
        setActiveLink(activeRootLink);
    }, [rootmenuId, menuItems]);

    const showBg = (menuItem: MenuItem) => (e: React.SyntheticEvent) =>
    {
        const activeBg = menuItem.dropdownBackgroundImage ? menuItem : menuItems.find((item) => item.id === rootmenuId);
        setActiveBg(activeBg);
        setActiveLink(menuItem);
    };

    const navLinkOnClick = (menuItem: MenuItem) => () =>
    {
        props.menuItemOnClick(menuItem);
    };

    const getBackgroundImages = useCallback((rootIndex: number) =>
    {
        const children = menuItems[rootIndex].children;
        if (children)
        {
            return children.map((child, imageIndex) =>
            {
                const image = child.dropdownBackgroundImage;
                if (image)
                {
                    const url = absoluteURL(typeof image === "string" ? image : getImageCropUrl(image.umbracoFile));
                    return (
                        <ImageCrop src={url}
                            alt={child.name} id={"img_" + (imageIndex + 1)} key={"img_" + (imageIndex + 1)}
                            className={currentBg?.id === child.id ? "active" : ""} width="100%" height="100%" />
                    );
                }
                return null;
            });
        }

        return null;
    }, [menuItems, currentBg]);

    const getRootBgImage = (rootMenuItem: MenuItem) =>
    {
        const rootBgImg = rootMenuItem.dropdownBackgroundImage;
        let rootImg = "";
        if (rootBgImg)
        {
            rootImg = absoluteURL(typeof rootBgImg === "string" ? rootBgImg : getImageCropUrl(rootBgImg.umbracoFile));
        }
        if (isDevelopment())
        {
            rootImg = rootImg.replace("localhost:5001", "backend.aarhusteater.dk");
        }
        return (
            <ImageCrop src={rootImg}
                alt={rootMenuItem.name} id={"img_0"} key={"img_0"}
                className={currentBg?.id === rootMenuItem.id ? "active" : ""} width="100%" height="100%" />
        );
    };

    return (
        <>
            {props.menuItems.map((menuItem: MenuItem, rootIndex) =>
                <CustomMenu key={rootIndex}
                    className={`custom-menu ${props.id === menuItem.id ? "custom-menu--active" : ""}`}
                    style={{
                        color: props.textColor,
                        backgroundColor: invert(props.textColor)
                    }}>
                    <div className="w-full sm:w-1/2 relative">
                        {menuItem.children ? menuItem.children.map((child: MenuItem, childIndex) => (
                            <React.Fragment key={"f" + childIndex}>
                                <NavLink
                                    onClick={navLinkOnClick(menuItem)}
                                    onMouseEnter={showBg(child)}
                                    activeClassName="button--active"
                                    className={currentLink?.id === child.id ? "active" : undefined}
                                    exact to={relativeURL(child.url)}>
                                    <span>{child.name}</span>
                                </NavLink>
                                {child.children && child.children.length > 0 ? <div className="sub-menu">
                                    <h4>{child.name}</h4>
                                    <ul>
                                        {child.children.map((subChild: MenuItem, subChildIndex) =>
                                            <li key={subChildIndex}>
                                                <NavLink to={relativeURL(subChild.url)} onClick={navLinkOnClick(menuItem)}>{subChild.name}</NavLink>
                                            </li>)
                                        }
                                    </ul>
                                </div> : null}
                                <br />
                            </React.Fragment>
                        )) : null}
                        {props.id === menuItem.id ? (
                            <div className="backgrounds">
                                {getRootBgImage(props.menuItems[rootIndex])}
                                {getBackgroundImages(rootIndex)}
                            </div>
                        ) : null}
                    </div>
                </CustomMenu>
            )}
        </>
    );
};

CustomMegadropdown.defaultProps = {
    menuItems: [],
};

export default CustomMegadropdown;